import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { JobsAPI } from '../../api/client';
import InferenceJobsTable from '../../components/Jobs/Inference/InferenceJobsTable';
import { useAuth } from '../../hooks/useAuth';
import PageWrapper from '../../components/Layout/PageWrapper';
import SkeletonLoader from '../../components/Common/SkeletonLoader';
import Pagination from '../../components/Common/Pagination';

const InferenceJobsListPage = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    count: 0,
    next: null,
    previous: null,
    currentPage: 1,
    totalPages: 1,
    pageSize: 20
  });

  const currentPage = parseInt(searchParams.get('page')) || 1;
  const currentPageSize = parseInt(searchParams.get('page_size')) || 20;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const params = {
          page: currentPage,
          page_size: currentPageSize
        };
        const data = await JobsAPI.list(params);

        if (Array.isArray(data)) {
          // Non-paginated response
          setJobs(data);
          setPagination({
            count: data.length,
            next: null,
            previous: null,
            currentPage: 1,
            totalPages: 1,
            pageSize: data.length
          });
        } else {
          // Paginated response
          setJobs(data.results || []);
          setPagination({
            count: data.count || 0,
            next: data.next,
            previous: data.previous,
            currentPage: currentPage,
            totalPages: Math.ceil((data.count || 0) / currentPageSize),
            pageSize: currentPageSize
          });
        }
      } catch (e) {
        setError('Failed to load jobs');
      } finally {
        setLoading(false);
      }
    };
    if (isAuthenticated) load();
  }, [isAuthenticated, currentPage, currentPageSize]);

  const handleNewJob = () => {
    navigate('/jobs/new');
  };

  const handlePageChange = (page) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', page.toString());
    setSearchParams(newParams);
  };

  const handlePageSizeChange = (pageSize) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page_size', pageSize.toString());
    newParams.set('page', '1'); // Reset to first page when changing page size
    setSearchParams(newParams);
  };

  if (!isAuthenticated) return null;

  return (
    <PageWrapper>
      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Experiments</h1>
            <p className="text-gray-600 mt-1">Manage and track your data processing experiments</p>
          </div>
          <button
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 shadow-sm hover:shadow-md"
            onClick={handleNewJob}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            New Experiment
          </button>
        </div>
      </div>

      {/* Content Section */}
      {loading ? (
        <SkeletonLoader type="jobsList" />
      ) : error ? (
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-medium text-red-800">Error loading experiments</h3>
              <p className="text-sm text-red-600 mt-1">{error}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <InferenceJobsTable jobs={jobs} />
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            totalCount={pagination.count}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            loading={loading}
          />
        </div>
      )}
    </PageWrapper>
  );
};

export default InferenceJobsListPage;
